import { calculatePercentage } from "@/utils/currencyUtils";
import { useMemo } from "react";

interface CalculationsInput {
    // Total negotiated amount (from Negotiation tab's latestOffer)
    totalNegotiated: number;

    // Reduction amounts
    providerReductions: number;
    insuranceReductions: number;
    costReduction: number;

    // Attorney settings
    attorneyFeePercentage: number | string | null;
    attorneyFeeManualOverride: number | string | null;

    // Client distribution override
    clientDistributionManualOverride: number | string | null;
}

export interface CalculationsOutput {
    attorneyFeeAmount: number;
    attorneyFeePercentage: number;
    clientDistributionAmount: number;
    isAttorneyFeeManual: boolean;
    isClientDistributionManual: boolean;
}

/**
 * Hook to calculate all disbursement values with proper error handling
 */
export const useDisbursementsCalculations = (input: CalculationsInput): CalculationsOutput => {
    const {
        totalNegotiated,
        providerReductions,
        insuranceReductions,
        costReduction,
        attorneyFeePercentage,
        attorneyFeeManualOverride,
        clientDistributionManualOverride,
    } = input;

    // Calculate attorney fee
    const { attorneyFeeAmount, isAttorneyFeeManual, effectivePercentage } = useMemo(() => {
        // If manual override exists, use it
        if (attorneyFeeManualOverride !== null && attorneyFeeManualOverride !== undefined) {
            const amount =
                typeof attorneyFeeManualOverride === "string"
                    ? Number.parseFloat(attorneyFeeManualOverride)
                    : attorneyFeeManualOverride;
            const percentage =
                typeof attorneyFeePercentage === "string"
                    ? Number.parseFloat(attorneyFeePercentage)
                    : attorneyFeePercentage;
            return {
                attorneyFeeAmount: amount,
                isAttorneyFeeManual: true,
                effectivePercentage: percentage ?? 33,
            };
        }

        // Otherwise calculate based on percentage (default 33%)
        const percentage =
            typeof attorneyFeePercentage === "string"
                ? Number.parseFloat(attorneyFeePercentage)
                : attorneyFeePercentage ?? 33;
        const calculatedFee = calculatePercentage(totalNegotiated, percentage);

        return {
            attorneyFeeAmount: calculatedFee,
            isAttorneyFeeManual: false,
            effectivePercentage: percentage,
        };
    }, [attorneyFeeManualOverride, attorneyFeePercentage, totalNegotiated]);

    // Calculate client distribution
    const { clientDistributionAmount, isClientDistributionManual } = useMemo(() => {
        // If manual override exists, use it
        if (
            clientDistributionManualOverride !== null &&
            clientDistributionManualOverride !== undefined
        ) {
            const amount =
                typeof clientDistributionManualOverride === "string"
                    ? Number.parseFloat(clientDistributionManualOverride)
                    : clientDistributionManualOverride;
            return {
                clientDistributionAmount: amount,
                isClientDistributionManual: true,
            };
        }

        // Otherwise calculate: Total - Provider Reductions - Insurance Reductions - COST - Attorney Fee
        const calculated =
            totalNegotiated -
            providerReductions -
            insuranceReductions -
            costReduction -
            attorneyFeeAmount;

        return {
            clientDistributionAmount: Math.max(0, calculated), // Never negative
            isClientDistributionManual: false,
        };
    }, [
        clientDistributionManualOverride,
        totalNegotiated,
        providerReductions,
        insuranceReductions,
        costReduction,
        attorneyFeeAmount,
    ]);

    return {
        attorneyFeeAmount,
        attorneyFeePercentage: effectivePercentage,
        clientDistributionAmount,
        isAttorneyFeeManual,
        isClientDistributionManual,
    };
};
